import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ConnectionProviderView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import {
  LIST_ROW,
  LIST_ROW_NAME,
  LIST_ROW_PHONE_END,
  LIST_ROW_PHONE_GRID,
  LIST_ROW_PHONE_NAME,
  LIST_ROW_PHONE_SECOND,
  LIST_ROW_PHONE_TEXT,
  LIST_ROW_SECONDARY,
  LIST_ROW_TRAILING,
} from '@/shared/components/listRow';

import { ProviderLogo } from './ProviderLogo';

export interface AvailableRowProps {
  provider: ConnectionProviderView;
  /** "Add another" for a multi-instance provider that already has one. */
  linkLabel: 'Connect' | 'Add another';
  onConnect: () => void;
}

/**
 * One provider the workspace could connect (spec 229 FR-003): logo, name,
 * what the connection gives, its category and a ghost link. Never a filled
 * button: the page's one filled action is the Add connection menu (FR-004).
 */
export function AvailableRow({
  provider,
  linkLabel,
  onConnect,
}: AvailableRowProps) {
  return (
    <li className={cn(LIST_ROW, LIST_ROW_PHONE_GRID)}>
      <ProviderLogo type={provider.type} />
      <span
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-0.5',
          LIST_ROW_PHONE_TEXT,
        )}
      >
        <span className={cn(LIST_ROW_NAME, LIST_ROW_PHONE_NAME)}>
          {provider.display_name}
        </span>
        <span
          className={cn(
            LIST_ROW_SECONDARY,
            'max-sm:whitespace-normal max-md:[overflow-wrap:anywhere]',
            LIST_ROW_PHONE_SECOND,
          )}
        >
          {provider.blurb}
        </span>
      </span>
      <span className={cn(LIST_ROW_TRAILING, LIST_ROW_PHONE_END)}>
        <Badge variant="outline" className="hidden shrink-0 sm:inline-flex">
          {provider.category}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          className="touch-target"
          aria-label={`${linkLabel} ${provider.display_name}`}
          onClick={onConnect}
        >
          {linkLabel}
        </Button>
      </span>
    </li>
  );
}
